import { toRaw } from 'vue';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const VARIABLE_PATTERN = /\{\{([^}]+)\}\}/g;

const extractVariables = text =>
  [...String(text || '').matchAll(VARIABLE_PATTERN)].map(match => match[1]);

const cloneTemplate = template => structuredClone(toRaw(template));

export const templateKey = template => template?.id || template?.name || '';

export const mediaHeader = template => {
  const header = findComponentByType(template, COMPONENT_TYPES.HEADER);
  return MEDIA_FORMATS.includes(header?.format) ? header : null;
};

export const templateExampleMediaUrl = template => {
  const value = mediaHeader(template)?.example?.header_handle?.[0];
  if (!value) return '';

  try {
    const url = new URL(String(value).trim());
    return url.protocol === 'https:' ? url.toString() : '';
  } catch (error) {
    return '';
  }
};

export const preferredTemplateMediaUrl = (template, currentUrl = '') =>
  currentUrl || templateExampleMediaUrl(template);

export const variableDescriptors = template => {
  const descriptors = [];
  const header = findComponentByType(template, COMPONENT_TYPES.HEADER);
  const body = findComponentByType(template, COMPONENT_TYPES.BODY);

  if (header?.format === 'TEXT') {
    extractVariables(header.text).forEach(key => {
      descriptors.push({ path: `header.${key}`, section: 'header', key });
    });
  }

  extractVariables(body?.text).forEach(key => {
    descriptors.push({ path: `body.${key}`, section: 'body', key });
  });

  const buttons = findComponentByType(template, COMPONENT_TYPES.BUTTONS);
  buttons?.buttons?.forEach((button, index) => {
    const requiresValue =
      button.type === 'COPY_CODE' ||
      (button.type === 'URL' && button.url?.includes('{{'));
    if (requiresValue) {
      descriptors.push({
        path: `buttons.${index}`,
        section: 'buttons',
        key: String(index + 1),
      });
    }
  });

  return descriptors;
};

export const initialMappings = (template, configuration = {}) => {
  const saved = new Map(
    (configuration.variables || []).map(item => [item.path, item])
  );
  return variableDescriptors(template).map(descriptor => ({
    ...descriptor,
    source: 'manual',
    keyValue: '',
    fixedValue: '',
    ...(saved.has(descriptor.path)
      ? {
          source: saved.get(descriptor.path).source,
          keyValue: saved.get(descriptor.path).key || '',
          fixedValue: saved.get(descriptor.path).value || '',
        }
      : {}),
  }));
};

export const serializeMappings = mappings =>
  mappings.map(mapping => ({
    path: mapping.path,
    source: mapping.source,
    ...(mapping.keyValue ? { key: mapping.keyValue } : {}),
    ...(mapping.source === 'fixed_text'
      ? { value: mapping.fixedValue || '' }
      : {}),
  }));

export const mergeProcessedParams = (target, source) => {
  Object.entries(source || {}).forEach(([section, values]) => {
    if (Array.isArray(values)) {
      target[section] ||= [];
      values.forEach((value, index) => {
        if (!value) return;
        target[section][index] = {
          ...(target[section][index] || {}),
          ...value,
        };
      });
      return;
    }

    target[section] = { ...(target[section] || {}), ...(values || {}) };
  });
};

export const previewVariables = processedParams => ({
  ...(processedParams?.header || {}),
  ...(processedParams?.body || {}),
});

export const templateWithMediaPreview = (template, mediaUrl) => {
  const copy = cloneTemplate(template);
  if (!mediaUrl) return copy;
  const header = findComponentByType(copy, COMPONENT_TYPES.HEADER);
  if (header && MEDIA_FORMATS.includes(header.format)) {
    header.example ||= {};
    header.example.header_handle = [mediaUrl];
  }
  return copy;
};

export const templateWithHeaderPreview = (template, headerValues = {}) => {
  const copy = cloneTemplate(template);
  const header = findComponentByType(copy, COMPONENT_TYPES.HEADER);
  if (header?.format !== 'TEXT') return copy;

  header.text = String(header.text || '').replace(
    VARIABLE_PATTERN,
    (match, key) => headerValues[key] || `[${key}]`
  );
  return copy;
};
